import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import usePageTitle from "@/hooks/usePageTitle";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Send, CheckCircle2, Globe, Search, Volume2, Camera, Video, Headphones, Monitor, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = {
  web: {
    icon: Globe, label: "Sites web & Applications", color: "#0099CC",
    projectTypes: ["Site vitrine", "Site e-commerce", "Application web", "Refonte site existant", "Landing page", "Portail client"],
    questions: [
      { id: "pages", label: "Nombre de pages estimees", type: "select", options: ["1-5 pages", "5-10 pages", "10-20 pages", "20+ pages"] },
      { id: "features", label: "Fonctionnalites souhaitees", type: "checkboxes", options: ["Formulaire de contact", "Blog", "Espace client", "Paiement en ligne", "Reservation", "Chat en direct", "Multi-langue"] },
      { id: "deadline", label: "Delai souhaite", type: "select", options: ["Urgent (< 2 semaines)", "1-2 mois", "3-6 mois", "Pas de deadline precise"] },
    ],
  },
  seo: {
    icon: Search, label: "SEO & Marketing digital", color: "#10B981",
    projectTypes: ["Audit SEO", "Referencement naturel", "Google Ads", "Reseaux sociaux", "Content marketing", "Email marketing"],
    questions: [
      { id: "website", label: "URL de votre site actuel", type: "text", placeholder: "https://www.votre-site.fr" },
      { id: "goals", label: "Objectifs principaux", type: "checkboxes", options: ["Plus de trafic", "Meilleur classement Google", "Plus de leads", "Notoriete de marque", "Reseaux sociaux", "Campagnes publicitaires"] },
      { id: "budget", label: "Budget mensuel envisage", type: "select", options: ["< 500 EUR/mois", "500-1000 EUR/mois", "1000-2500 EUR/mois", "2500+ EUR/mois"] },
    ],
  },
  sonorisation: {
    icon: Volume2, label: "Sonorisation evenementielle", color: "#F59E0B",
    projectTypes: ["Conference", "Concert", "Salon professionnel", "Mariage", "Seminaire", "Festival", "Ceremonie", "Autre evenement"],
    questions: [
      { id: "guests", label: "Nombre de personnes attendues", type: "select", options: ["< 50", "50-150", "150-500", "500-1000", "1000+"] },
      { id: "equipments", label: "Besoins en equipement", type: "checkboxes", options: ["Micros sans fil", "Enceintes", "Table de mixage", "Eclairage", "DJ", "Retour scene", "Enregistrement live"] },
      { id: "event_date", label: "Date de l'evenement", type: "text", placeholder: "JJ/MM/AAAA" },
      { id: "venue", label: "Lieu de l'evenement", type: "text", placeholder: "Adresse ou nom du lieu" },
    ],
  },
  reportage: {
    icon: Camera, label: "Reportage photo & video", color: "#8B5CF6",
    projectTypes: ["Reportage photo", "Captation video", "Aftermovie", "Livestream", "Drone aerien", "Clip promotionnel", "Couverture evenement"],
    questions: [
      { id: "duration", label: "Duree de la prestation", type: "select", options: ["Demi-journee", "Journee complete", "2 jours", "3+ jours"] },
      { id: "deliverables", label: "Livrables souhaites", type: "checkboxes", options: ["Photos HD", "Video montee", "Aftermovie court", "Livestream", "Photos drone", "Teaser reseaux sociaux"] },
      { id: "event_date", label: "Date de l'evenement", type: "text", placeholder: "JJ/MM/AAAA" },
    ],
  },
  regie: {
    icon: Video, label: "Regie audiovisuelle", color: "#EC4899",
    projectTypes: ["Conference filmee", "Emission live", "Webinaire", "Table ronde", "Spectacle", "Evenement hybrid", "Formation"],
    questions: [
      { id: "screens", label: "Configuration video", type: "checkboxes", options: ["Multi-cameras", "Ecran geant", "Retransmission live", "Enregistrement", "Fond vert", "Prompteur", "Sous-titrage en direct"] },
      { id: "guests", label: "Nombre de personnes attendues", type: "select", options: ["< 50", "50-200", "200-500", "500+"] },
      { id: "event_date", label: "Date de l'evenement", type: "text", placeholder: "JJ/MM/AAAA" },
    ],
  },
  captation: {
    icon: Headphones, label: "Captation audio", color: "#06B6D4",
    projectTypes: ["Podcast", "Voix off", "Enregistrement conference", "Captation musicale", "Doublage", "Audio branding"],
    questions: [
      { id: "format", label: "Format de sortie", type: "checkboxes", options: ["Podcast", "Voix off publicitaire", "Enregistrement studio", "Captation live", "Montage audio", "Mastering"] },
      { id: "duration", label: "Duree estimee", type: "select", options: ["< 30 min", "30 min - 1h", "1h - 3h", "3h+"] },
    ],
  },
  evenementiel: {
    icon: CalendarDays, label: "Evenementiel global", color: "#F59E0B",
    projectTypes: ["Conference", "Salon professionnel", "Gala", "Seminaire", "Festival", "Mariage", "Lancement de produit", "Team building"],
    questions: [
      { id: "prestations", label: "Prestations souhaitees", type: "checkboxes", options: ["Sonorisation", "Photo & Video", "Regie audiovisuelle", "Captation audio", "Decoration", "Traiteur", "Securite", "Communication"] },
      { id: "guests", label: "Nombre d'invites", type: "select", options: ["< 50", "50-150", "150-500", "500-1000", "1000+"] },
      { id: "event_date", label: "Date de l'evenement", type: "text", placeholder: "JJ/MM/AAAA" },
      { id: "venue", label: "Lieu", type: "text", placeholder: "Adresse ou nom du lieu" },
      { id: "budget", label: "Budget global estimatif", type: "select", options: ["< 2000 EUR", "2000-5000 EUR", "5000-15000 EUR", "15000-50000 EUR", "50000+ EUR"] },
    ],
  },
};

export default function QuotePage() {
  usePageTitle("Demander un devis");
  const [params] = useSearchParams();
  const preselected = params.get("service") || "";
  const [selectedService, setSelectedService] = useState(preselected || "");
  const [step, setStep] = useState(preselected ? 2 : 1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ project_type: "", name: "", phone: "", email: "", city: "", message: "" });
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (preselected && SERVICES[preselected]) {
      setSelectedService(preselected);
      setStep(2);
    }
  }, [preselected]);

  const svc = SERVICES[selectedService];

  const updateAnswer = (qId, value) => setAnswers(a => ({ ...a, [qId]: value }));
  const toggleCheckbox = (qId, option) => {
    setAnswers(a => {
      const current = a[qId] || [];
      return { ...a, [qId]: current.includes(option) ? current.filter(x => x !== option) : [...current, option] };
    });
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error("Nom, email et telephone sont obligatoires"); return; }
    setSubmitting(true);
    try {
      const notes = Object.entries(answers).map(([k, v]) => {
        const q = svc?.questions?.find(q => q.id === k);
        const label = q?.label || k;
        const val = Array.isArray(v) ? v.join(", ") : v;
        return `${label}: ${val}`;
      }).join("\n");

      await axios.post(`${API}/leads`, {
        name: form.name, email: form.email, phone: form.phone, city: form.city,
        project_type: form.project_type || svc?.label || selectedService,
        source: `Formulaire devis — ${svc?.label || "General"}`,
        notes: `Service: ${svc?.label || selectedService}\nType: ${form.project_type}\n${notes}\n\nMessage: ${form.message}`.trim(),
      });
      setSubmitted(true);
      toast.success("Demande envoyee ! Nous vous recontactons sous 24h.");
    } catch { toast.error("Erreur lors de l'envoi"); }
    finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="container mx-auto px-6 py-20 max-w-2xl text-center" data-testid="quote-success">
      <CheckCircle2 className="h-16 w-16 text-[#10B981] mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white">Demande envoyee !</h1>
      <p className="text-[#94A3B8] mt-2">Notre equipe vous recontactera sous 24 heures.</p>
      <div className="mt-6 flex gap-3 justify-center">
        <Link to="/" className="text-[#0099CC] hover:underline text-sm">Retour a l'accueil</Link>
        <Link to="/evenements" className="text-[#F59E0B] hover:underline text-sm">Decouvrir Event Center</Link>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-6 py-14 max-w-3xl" data-testid="quote-page">
      <h1 className="text-3xl sm:text-4xl font-bold text-white">Demander un devis</h1>
      <p className="mt-2 text-[#94A3B8]">Choisissez votre prestation et obtenez une estimation personnalisee.</p>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mt-6 mb-8">
        {["Service", "Details", "Coordonnees"].map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${step > i + 1 ? "bg-[#10B981] text-white" : step === i + 1 ? "bg-[#0099CC] text-white" : "bg-[#1E293B] text-[#64748b]"}`}>{i + 1}</div>
            <span className={`text-xs hidden sm:block ${step === i + 1 ? "text-white" : "text-[#64748b]"}`}>{label}</span>
            {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-[#10B981]" : "bg-[#1E293B]"}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Choose service */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Quel service vous interesse ?</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {Object.entries(SERVICES).map(([key, s]) => (
              <button
                key={key}
                data-testid={`service-${key}`}
                onClick={() => { setSelectedService(key); setStep(2); }}
                className={`p-4 rounded-xl border-2 text-left transition-all ${selectedService === key ? "border-[#0099CC] bg-[#0099CC]/10" : "border-[#2A3441] hover:border-[#0099CC]/40 bg-[#151B2B]"}`}
              >
                <s.icon className="h-5 w-5 mb-2" style={{ color: s.color }} />
                <div className="text-white font-medium text-sm">{s.label}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Service-specific details */}
      {step === 2 && svc && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <svc.icon className="h-5 w-5" style={{ color: svc.color }} />
            <h2 className="text-lg font-semibold text-white">{svc.label}</h2>
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Type de projet *</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {svc.projectTypes.map(pt => (
                <button key={pt} onClick={() => setForm(f => ({ ...f, project_type: pt }))}
                  className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${form.project_type === pt ? "bg-[#0099CC]/20 border-[#0099CC] text-white border" : "bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white hover:border-[#0099CC]/40"}`}
                  data-testid={`project-type-${pt.replace(/\s+/g, '-').toLowerCase()}`}
                >{pt}</button>
              ))}
            </div>
          </div>

          {svc.questions.map(q => (
            <div key={q.id} className="space-y-2">
              <Label className="text-[#94A3B8]">{q.label}</Label>
              {q.type === "text" && (
                <Input value={answers[q.id] || ""} onChange={e => updateAnswer(q.id, e.target.value)} placeholder={q.placeholder || ""} className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid={`q-${q.id}`} />
              )}
              {q.type === "select" && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {q.options.map(opt => (
                    <button key={opt} onClick={() => updateAnswer(q.id, opt)}
                      className={`px-3 py-2 rounded-lg text-xs text-left transition-all ${answers[q.id] === opt ? "bg-[#0099CC]/20 border-[#0099CC] text-white border" : "bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white"}`}
                    >{opt}</button>
                  ))}
                </div>
              )}
              {q.type === "checkboxes" && (
                <div className="flex flex-wrap gap-2">
                  {q.options.map(opt => (
                    <button key={opt} onClick={() => toggleCheckbox(q.id, opt)}
                      className={`px-3 py-1.5 rounded-lg text-xs transition-all ${(answers[q.id] || []).includes(opt) ? "bg-[#0099CC] text-white" : "bg-[#151B2B] border border-[#2A3441] text-[#94A3B8] hover:text-white"}`}
                    >{opt}</button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Message complementaire (optionnel)</Label>
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:border-[#0099CC]" placeholder="Precisez vos besoins, contraintes ou questions..." data-testid="q-message" />
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#2A3441] text-[#94A3B8]"><ArrowLeft className="h-4 w-4 mr-1" /> Changer de service</Button>
            <Button onClick={() => setStep(3)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">Continuer <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Contact info */}
      {step === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Vos coordonnees</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2"><Label className="text-[#94A3B8]">Nom complet *</Label><Input data-testid="q-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="bg-[#0B0F19] border-[#2A3441] text-white" /></div>
            <div className="space-y-2"><Label className="text-[#94A3B8]">Email *</Label><Input data-testid="q-email" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-[#0B0F19] border-[#2A3441] text-white" /></div>
            <div className="space-y-2"><Label className="text-[#94A3B8]">Telephone *</Label><Input data-testid="q-phone" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="bg-[#0B0F19] border-[#2A3441] text-white" /></div>
            <div className="space-y-2"><Label className="text-[#94A3B8]">Ville</Label><Input data-testid="q-city" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="bg-[#0B0F19] border-[#2A3441] text-white" /></div>
          </div>

          <div className="flex items-start gap-3">
            <input type="checkbox" id="rgpd-devis" required className="mt-1 accent-[#0099CC]" data-testid="rgpd-checkbox" />
            <label htmlFor="rgpd-devis" className="text-xs text-[#64748b]">
              J'accepte que mes donnees soient traitees conformement a la <a href="/politique-confidentialite" target="_blank" className="text-[#0099CC] hover:underline">politique de confidentialite</a>. *
            </label>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="border-[#2A3441] text-[#94A3B8]"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#0099CC] hover:bg-[#0088B5] text-white"><Send className="h-4 w-4 mr-1" /> {submitting ? "Envoi..." : "Envoyer la demande"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
